import { Link } from "react-router-dom";
import { SocialLinks } from "../components/SocialLinks";
import { ADMISSION_CONTACTS } from "../lib/admissionContacts";
import { INSTITUTE_REACH, directionsUrl, mapEmbedUrl } from "../lib/contact";
import { DEANS, deanPhoto } from "../lib/deans";
import { EXAM_DEAN_REACH } from "../lib/examOffice";
import { HOSTELS } from "../lib/hostels";
import { LIBRARY_STAFF } from "../lib/library";
import { PLACEMENT_CONTACTS } from "../lib/placementCell";
import { ROUTES } from "../lib/routes";
import { activeSocial } from "../lib/social";
import { STAFF } from "../lib/staff";
import { Figure } from "../components/Figure";
import type { Media } from "@zivin-labs/site-content";

/** Initials for a member with no portrait, honorifics stripped. */
function initials(name: string): string {
  const parts = name
    .replace(/^(Prof\.?|Dr\.?|Sri|Smt\.?|Mr\.?|Ms\.?|Mrs\.?)\s*/i, "")
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/** Portrait from the staff directory, where a verified slug exists. */
const staffPhoto = (slug?: string): Media | undefined =>
  slug ? STAFF.find((m) => m.slug === slug)?.photo : undefined;

/** One person in the directory: short label, name, portrait, published reach. */
interface OfficePerson {
  label: string;
  name: string;
  photo?: Media;
  phone?: string;
  email?: string;
}

interface OfficeBlock {
  ctype: string;
  title: string;
  to: string;
  people: OfficePerson[];
}

const EXAM_DEAN = DEANS.find((d) => d.role === "Dean Examination");

/** "Dean, Admissions" -> "Dean"; qualification tails ("…, M.Sc, Ph.D.") dropped. */
const shortRole = (r: string) => r.split(",")[0].trim();
const shortName = (n: string) => n.split(/,\s*(?=[A-Z]\.|M\.|Ph)/)[0].replace(/,$/, "").trim();

/**
 * Every entry is drawn from the data module that owns that office —
 * admissionContacts, placementCell, examOffice + deans, library, hostels — so
 * this page can never drift from the pages it points at. The prototype's
 * "233 8xx" extensions, invented mailboxes (coe@, library@, hostel@),
 * "Prof. Coord-01" and block/floor locations are gone, not replaced: none of
 * them are published anywhere, and a wrong number routes a real call into a
 * void.
 *
 * Each office shows its principal contact(s) only — the Dean of Admissions, not
 * the Principal and Director who also sit on that page; the Librarian, not the
 * assistants. The "Full page" link carries the complete roster. Source modules
 * are untouched, so /admissions, /placements and /library still show everyone.
 */
const OFFICES: OfficeBlock[] = [
  {
    ctype: "Administration",
    title: "Principal & Director",
    to: `${ROUTES.about}#leadership`,
    people: ADMISSION_CONTACTS.filter(
      (c) => c.role === "Principal" || c.role === "Director",
    ).map((c) => ({
      label: c.role,
      name: c.name,
      photo: staffPhoto(c.staffSlug),
      phone: c.phone,
      email: c.email,
    })),
  },
  {
    ctype: "Academic Deans",
    title: "Deans, Academics",
    to: `${ROUTES.about}#deans`,
    // The deans roster publishes no direct reach; the row carries who they are,
    // and the office link leads to the full deans list.
    people: DEANS.filter((d) => d.role.includes("Academics")).map((d) => ({
      label: d.role.replace(" Academics", ""),
      name: d.name,
      photo: deanPhoto(d),
    })),
  },
  {
    ctype: "Admissions",
    title: "Applying to BIET",
    to: ROUTES.admissions,
    people: ADMISSION_CONTACTS.filter((c) => c.role === "Dean, Admissions").map((c) => ({
      label: shortRole(c.role),
      name: c.name,
      photo: staffPhoto(c.staffSlug),
      phone: c.phone,
      email: c.email,
    })),
  },
  {
    ctype: "Placements",
    title: "Training & Placement Cell",
    to: ROUTES.placements,
    people: PLACEMENT_CONTACTS.filter((c) => c.name !== "Prof. Raghavendra N T").map((c) => ({
      label: shortRole(c.role).replace("Placement Officer", "Officer"),
      name: c.name,
      photo: staffPhoto(c.staffSlug),
      phone: c.phone,
      email: c.email,
    })),
  },
  {
    ctype: "Examinations",
    title: "Controller of Examinations",
    to: ROUTES.examinations,
    people: EXAM_DEAN
      ? [
          {
            label: "Dean",
            name: EXAM_DEAN.name,
            photo: deanPhoto(EXAM_DEAN),
            phone: EXAM_DEAN_REACH.phone,
            email: EXAM_DEAN_REACH.email,
          },
        ]
      : [],
  },
  {
    ctype: "Library",
    title: "Central Library",
    to: ROUTES.library,
    people: LIBRARY_STAFF.filter((m) => m.designation === "Librarian").map((m) => ({
      label: shortRole(m.designation),
      name: shortName(m.name),
      photo: staffPhoto(m.staffSlug),
      phone: m.phone,
      email: m.email,
    })),
  },
  {
    ctype: "Hostels",
    title: "Hostel wardens",
    to: ROUTES.hostels,
    people: HOSTELS.flatMap((h) => {
      const warden = h.contacts.find((c) => c.role === "Warden" && c.phone);
      return warden
        ? [
            {
              label: h.gender.replace("'", "\u2019"),
              name: warden.name,
              // Only the boys' warden has a verified staff match; see campusLife's
              // exact-match rule — a near-match risks the wrong face.
              photo: staffPhoto(warden.name === "Dr. G. Manavendra" ? "g-manavendra" : undefined),
              phone: warden.phone,
            },
          ]
        : [];
    }),
  },
];

/**
 * Topic → the address the institute publishes for that office. The form has no
 * backend; submitting composes a mailto to the right desk, so a message cannot
 * silently go nowhere (the old form's submit was alert("illustrative only")).
 * Transcripts go to the Dean Examination's published address.
 */
const ENQUIRY_ROUTES: Record<string, string> = {
  "Undergraduate admissions": "admissions@bietdvg.edu",
  "Postgraduate admissions": "admissions@bietdvg.edu",
  "Hostel availability": "admissions@bietdvg.edu",
  "Transcripts & verification": EXAM_DEAN_REACH.email,
  "Placements & training": "placement@bietdvg.edu",
  "Recruiter — campus visit": "placement@bietdvg.edu",
  "General enquiry": INSTITUTE_REACH.emails.principal,
};

function composeEnquiry(e: React.FormEvent<HTMLFormElement>) {
  e.preventDefault();
  const f = new FormData(e.currentTarget);
  const topic = String(f.get("topic") || "General enquiry");
  const name = [f.get("first"), f.get("last")].filter(Boolean).join(" ");
  const lines = [
    String(f.get("message") || ""),
    "",
    `— ${name}`,
    f.get("email") ? `Email: ${f.get("email")}` : "",
    f.get("phone") ? `Phone: ${f.get("phone")}` : "",
  ].filter(Boolean);
  const to = ENQUIRY_ROUTES[topic] ?? INSTITUTE_REACH.emails.principal;
  const subject = encodeURIComponent(`${topic} — ${name}`);
  const body = encodeURIComponent(lines.join("\n"));
  window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
}

export default function Contact() {
  return (
    <>
      <section className="contact-hero">
        <div className="container contact-hero-grid">
          <div>
            <div className="kicker eyebrow" style={{ color: "var(--gold-400)" }}>Reach BIET</div>
            <h1>Visit our campus.</h1>
            <p className="lead">
              {INSTITUTE_REACH.address.join(", ")} — a 63-acre campus in central Karnataka, 6 km
              from the heart of Davanagere.
            </p>
            <div style={{ display: "flex", gap: 24, marginTop: 32, flexWrap: "wrap" }}>
              <a className="btn btn-primary" href="#offices">Who to contact <span>→</span></a>
              <a className="btn btn-on-dark" href={INSTITUTE_REACH.tourVideo} target="_blank" rel="noreferrer">
                Campus tour · video
              </a>
              <a className="btn btn-on-dark" href="#enquiry">Write to us</a>
            </div>
          </div>
          <div className="hotline-card">
            <div className="label">The institute's line</div>
            <span className="number">
              {INSTITUTE_REACH.phone}
              <small>Fax · {INSTITUTE_REACH.fax.join(" / ")}</small>
            </span>
            <div className="hours">
              Email:{" "}
              <a
                href={`mailto:${INSTITUTE_REACH.emails.principal}`}
                style={{ color: "var(--navy-900)", textDecoration: "underline" }}
              >
                {INSTITUTE_REACH.emails.principal}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      <section className="section" id="offices">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Who to contact</div>
              <h2>The right office, first time.</h2>
            </div>
          </div>

          {/* Same table design as the SAC council on /campus-life: the office
              spans its rows, a portrait sits beside each name, and the reach
              column carries the published phone and address. */}
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Office</th>
                  <th scope="col">Member</th>
                  <th scope="col">Reach</th>
                </tr>
              </thead>
              <tbody>
                {OFFICES.filter((o) => o.people.length > 0).flatMap((o) =>
                  o.people.map((p, i) => (
                    <tr key={`${o.ctype}-${p.name}-${p.label}`}>
                      {i === 0 && (
                        <th scope="rowgroup" rowSpan={o.people.length} className="cr-office">
                          {o.ctype}
                          <Link className="cr-office-link" to={o.to}>
                            Full page <span className="arr">→</span>
                          </Link>
                        </th>
                      )}
                      <td className="cr-name">
                        <span className="cr-person">
                          {p.photo ? (
                            <Figure
                              media={p.photo}
                              fallbackLabel={p.name}
                              aspect="1/1"
                              className="cr-avatar"
                              sizes="40px"
                            />
                          ) : (
                            <span className="cr-avatar cr-avatar--initials" aria-hidden>
                              {initials(p.name)}
                            </span>
                          )}
                          <span>
                            {p.name}
                            <span className="cr-sub">{p.label}</span>
                          </span>
                        </span>
                      </td>
                      <td className="cr-reach">
                        {p.email && <a href={`mailto:${p.email}`}>{p.email}</a>}
                        {!p.email && (
                          <span style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>

        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      <section className="section section--flush" id="location">
        <div className="container">
          <div className="kicker">Location</div>
          <h2 style={{ fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600, letterSpacing: "-0.01em", maxWidth: "26ch", marginTop: 12 }}>
            Shamanur Road, Davanagere.
          </h2>
          <div className="map-cta reveal">
            <div className="map-block">
              <div className="map-stamp">
                <div className="label">Main Campus</div>
                <div className="name">BIET — {INSTITUTE_REACH.name}</div>
                <div className="addr">{INSTITUTE_REACH.address.join(" · ")}</div>
              </div>
              <iframe
                title="BIET location"
                src={mapEmbedUrl()}
                loading="lazy"
                referrerPolicy="no-referrer"
              ></iframe>
            </div>

            <div className="map-cta-panel">
              <div className="kicker">Plan a visit</div>
              <h3>Come see the campus.</h3>
              <p>
                The campus is 6 km from the heart of the city; city buses run to the college from
                almost all areas of Davanagere.
              </p>
              <div className="map-cta-facts">
                <div className="dmc-block" style={{ borderBottom: "none", paddingBottom: 0 }}>
                  <div className="dmc-label">Address</div>
                  <div className="dmc-value">{INSTITUTE_REACH.address.join(", ")}</div>
                </div>
                <div className="dmc-block" style={{ borderBottom: "none", paddingBottom: 0 }}>
                  <div className="dmc-label">Phone</div>
                  <div className="dmc-value">
                    <a href={`tel:${INSTITUTE_REACH.phone.replace(/\s/g, "")}`}>
                      {INSTITUTE_REACH.phone}
                    </a>
                  </div>
                </div>
              </div>
              <div className="map-cta-actions">
                <a className="btn btn-primary" href={directionsUrl()} target="_blank" rel="noreferrer">
                  Get directions <span>→</span>
                </a>
                <a className="btn btn-ghost" href={INSTITUTE_REACH.tourVideo} target="_blank" rel="noreferrer">
                  Campus tour · video
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      <section className="section section--tight section--flush" id="enquiry">
        <div className="container">
          <div className="inquiry-card">
            <div className="inquiry-grid">
              <div>
                <div className="kicker">Send us a message</div>
                <h2 style={{ marginTop: 12, fontSize: "clamp(24px, 2.4vw, 32px)", fontWeight: 600, maxWidth: "20ch" }}>
                  Or write to us, and we'll route it.
                </h2>
                <p style={{ color: "var(--text-soft)", marginTop: 14, fontSize: 15 }}>
                  Pick a topic and your message goes to the office the institute publishes for it —
                  submitting opens the email in your own mail app, addressed and ready to send.
                </p>
              </div>
              <form className="inquiry-form" onSubmit={composeEnquiry}>
                <div><label>First name</label><input name="first" type="text" required /></div>
                <div><label>Last name</label><input name="last" type="text" /></div>
                <div><label>Email</label><input name="email" type="email" /></div>
                <div><label>Phone</label><input name="phone" type="tel" /></div>
                <div className="span2">
                  <label>I'm asking about</label>
                  <select name="topic">
                    {Object.keys(ENQUIRY_ROUTES).map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="span2">
                  <label>Your message</label>
                  <textarea name="message" rows={4} required></textarea>
                </div>
                <div className="span2">
                  <button type="submit" className="btn btn-primary" style={{ border: "none", cursor: "pointer" }}>
                    Compose email <span>→</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* id is the target of the top bar's "Social Media Hub" link. Renders
          nothing until an account in lib/social.ts has a URL. */}
      {activeSocial().length > 0 && (
        <section className="section section--tight section--flush" id="social">
          <div className="container">
            <SocialLinks variant="cards" />
          </div>
        </section>
      )}
    </>
  );
}
